import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Star, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Vendor } from '../../../types/vendor';
import { useNavigate } from 'react-router-dom';

interface SearchBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  vendors: Record<string, Vendor[]>;
}

// Helper function to create a URL-friendly slug from a name
const createSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-')         // Replace spaces with hyphens
    .replace(/-+/g, '-');         // Replace multiple hyphens with single hyphen
};

const SearchBar: React.FC<SearchBarProps> = ({
  searchQuery,
  setSearchQuery,
  showFilters,
  setShowFilters,
  vendors,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const placeholders = [
    "Decor in Dallas",
    "Florists in Dallas",
    "Photographers in Dallas",
    "Venues with Gardens in Dallas",
    "Cake Designers in Dallas",
    "Planners in Austin",
  ];

  // Animate placeholder text
  useEffect(() => {
    if (!isFocused) {
      const interval = setInterval(() => {
        setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isFocused]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Enhanced search filtering
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const allVendors = Object.values(vendors).flat();
      const searchTerms = searchQuery.toLowerCase().split(' ');
      
      const filtered = allVendors.filter(vendor => {
        // Create a searchable text combining all relevant vendor fields
        const searchableText = [
          vendor.name,
          vendor.vendorType,
          vendor.specialty,
          vendor.location,
          vendor.address,
          vendor.bio,
          vendor.locationBio,
          ...vendor.aboutAttributes.map(attr => 
            `${attr.aboutItemName} ${attr.aboutItemDescription}`
          ),
          ...vendor.services.map(service => 
            `${service.serviceType} ${service.serviceDescriptions.join(' ')}`
          )
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        // Check if all search terms are found in the searchable text
        return searchTerms.every(term => searchableText.includes(term));
      })
      .sort((a, b) => {
        // Prioritize exact matches in name and vendorType
        const aExactMatch = a.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          a.vendorType.toLowerCase().includes(searchQuery.toLowerCase());
        const bExactMatch = b.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          b.vendorType.toLowerCase().includes(searchQuery.toLowerCase());
        
        if (aExactMatch && !bExactMatch) return -1;
        if (!aExactMatch && bExactMatch) return 1;
        
        // Secondary sort by rating
        return (b.rating || 0) - (a.rating || 0);
      })
      .slice(0, 5); // Limit to 5 results

      setFilteredVendors(filtered);
      setShowDropdown(true);
    } else {
      setFilteredVendors([]);
      setShowDropdown(false);
    }
  }, [searchQuery, vendors]);

  // Handle search submission (Enter key or button click)
  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      // Navigate to search results page with all matching vendors
      const allVendors = Object.values(vendors).flat();
      const searchTerms = searchQuery.toLowerCase().split(' ');
      
      const allFilteredVendors = allVendors.filter(vendor => {
        const searchableText = [
          vendor.name,
          vendor.vendorType,
          vendor.specialty,
          vendor.location,
          vendor.address,
          vendor.bio,
          vendor.locationBio,
          ...vendor.aboutAttributes.map(attr => 
            `${attr.aboutItemName} ${attr.aboutItemDescription}`
          ),
          ...vendor.services.map(service => 
            `${service.serviceType} ${service.serviceDescriptions.join(' ')}`
          )
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchTerms.every(term => searchableText.includes(term));
      })
      .sort((a, b) => {
        const aExactMatch = a.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          a.vendorType.toLowerCase().includes(searchQuery.toLowerCase());
        const bExactMatch = b.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          b.vendorType.toLowerCase().includes(searchQuery.toLowerCase());
        
        if (aExactMatch && !bExactMatch) return -1;
        if (!aExactMatch && bExactMatch) return 1;
        
        return (b.rating || 0) - (a.rating || 0);
      });

      // Navigate to search results page with state
      navigate('/connect/search', {
        state: {
          aiSearchResults: {
            vendors: allFilteredVendors,
            query: searchQuery,
            totalResults: allFilteredVendors.length
          }
        }
      });
      
      setShowDropdown(false);
      setSearchQuery('');
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="bg-white rounded-lg shadow-sm">
        <div className="flex items-center gap-1.5">
          <div className="flex-grow relative">
            <div className="relative">
              <input
                type="text"
                placeholder={placeholders[placeholderIndex]}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                className={`w-full h-8 rounded-lg py-1 px-3 pl-8 
                  bg-gradient-to-r from-white to-white/95
                  text-gray-700 placeholder-gray-400
                  shadow-sm
                  border-none
                  focus:outline-none text-xs
                  transition-all duration-200
                  placeholder:transition-opacity placeholder:duration-200
                  ${isFocused ? 'shadow-[0_0_0_2px_rgba(255,78,142,0.1)] ring-1 ring-[#FF4E8E]' : 'hover:shadow-md'}`}
              />
              <Search 
                className={`absolute left-2.5 top-2 w-3.5 h-3.5 transition-colors duration-200 ${isFocused ? 'text-[#FF4E8E]' : 'text-gray-400'}`}
              />
            </div>

            {/* Search Results Dropdown */}
            <AnimatePresence>
              {showDropdown && filteredVendors.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute w-full mt-2 bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 max-h-[60vh] overflow-y-auto z-50"
                >
                  {filteredVendors.map((vendor) => (
                    <motion.div
                      key={vendor.id}
                      whileTap={{ backgroundColor: '#FFF1F6' }}
                      className="p-4 md:p-3 cursor-pointer border-b border-gray-100 last:border-0 active:bg-[#FFF1F6] hover:bg-[#FFF1F6]/50 transition-colors touch-manipulation"
                      onClick={() => {
                        navigate(`/connect/vendor/${vendor.id}/${createSlug(vendor.name)}`);
                        setShowDropdown(false);
                        setSearchQuery('');
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                          <img 
                            src={vendor.image || '/themesOption/placeholder.jpg'} 
                            alt={vendor.name} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-grow min-w-0">
                          <div className="flex items-start gap-2 flex-wrap">
                            <h4 className="text-base font-medium text-gray-800 truncate">{vendor.name}</h4>
                            <span className="px-2 py-1 rounded-full text-xs bg-[#FFF1F6] text-[#FF4E8E] whitespace-nowrap">
                              {vendor.vendorType}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-gray-600 text-sm mt-1 flex-wrap">
                            <div className="flex items-center gap-1 min-w-0">
                              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate">{vendor.location}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 flex-shrink-0" />
                              <span>{vendor.rating}</span>
                            </div>
                          </div>
                          {vendor.specialty && (
                            <div className="mt-1 text-xs text-gray-500 truncate">
                              Specialty: {vendor.specialty}
                            </div>
                          )}
                        </div>
                        <span className="text-[#FF4E8E] text-sm whitespace-nowrap">{vendor.price}</span>
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* View All Results Button */}
                  {filteredVendors.length > 0 && (
                    <motion.div
                      whileTap={{ backgroundColor: '#FF4E8E' }}
                      className="p-3 cursor-pointer bg-gray-50 hover:bg-[#FF4E8E] hover:text-white transition-colors text-center border-t border-gray-100"
                      onClick={handleSearchSubmit}
                    >
                      <div className="text-sm font-medium">
                        View All Results ({Object.values(vendors).flat().filter(vendor => {
                          const searchTerms = searchQuery.toLowerCase().split(' ');
                          const searchableText = [
                            vendor.name,
                            vendor.vendorType,
                            vendor.specialty,
                            vendor.location,
                            vendor.address,
                            vendor.bio,
                            vendor.locationBio,
                            ...vendor.aboutAttributes.map(attr => 
                              `${attr.aboutItemName} ${attr.aboutItemDescription}`
                            ),
                            ...vendor.services.map(service => 
                              `${service.serviceType} ${service.serviceDescriptions.join(' ')}`
                            )
                          ]
                            .filter(Boolean)
                            .join(' ')
                            .toLowerCase();
                          return searchTerms.every(term => searchableText.includes(term));
                        }).length})
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        
        {/* Expandable filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-4"
            >
              <div className="grid grid-cols-1 gap-4 pt-4 border-t border-gray-100">
                <div>
                  <h4 className="text-[#FF4E8E] text-sm font-medium mb-3">Price Range</h4>
                  <div className="flex flex-wrap gap-2">
                    {['$', '$$', '$$$', '$$$$', '$$$$$'].map((price) => (
                      <button
                        key={price}
                        className="bg-[#FFF1F6] active:bg-[#FFF1F6]/80 rounded-full px-6 py-2.5 text-gray-700 text-base touch-manipulation"
                      >
                        {price}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-[#FF4E8E] text-sm font-medium mb-3">Rating</h4>
                  <div className="flex flex-wrap gap-2">
                    {[4, 4.5, 4.8].map((rating) => (
                      <button
                        key={rating}
                        className="bg-[#FFF1F6] active:bg-[#FFF1F6]/80 rounded-full px-6 py-2.5 text-gray-700 text-base flex items-center touch-manipulation"
                      >
                        {rating}+ <Star className="w-4 h-4 ml-1 text-amber-400 fill-amber-400" />
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-[#FF4E8E] text-sm font-medium mb-3">Availability</h4>
                  <div className="flex flex-wrap gap-2">
                    {['Spring', 'Summer', 'Fall', 'Winter', 'Year-round'].map((season) => (
                      <button
                        key={season}
                        className="bg-[#FFF1F6] active:bg-[#FFF1F6]/80 rounded-full px-6 py-2.5 text-gray-700 text-base touch-manipulation"
                      >
                        {season}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SearchBar; 